import PropTypes from 'prop-types';
import React, { Component } from 'react';
import cx from 'classnames';
import ExecutionEnvironment from 'fbjs/lib/ExecutionEnvironment';

import Dismiss from './Dismiss';
import Button from 'components/Button';

import styles from './ModalDialog.css';

class ModalDialog extends Component {
	constructor(props) {
		super(props);

		this.handleKeyDown = this.handleKeyDown.bind(this);
		this.onShow = this.onShow.bind(this);
		this.onHide = this.onHide.bind(this);
		this.focus = this.focus.bind(this);
		this.restoreFocus = this.restoreFocus.bind(this);
		this.handleOverlayClick = this.handleOverlayClick.bind(this);
	}

	componentDidMount() {
		if (this.props.active) this.onShow();
	}

	componentDidUpdate(prevProps) {
		if (!prevProps.active && this.props.active) {
			this.onShow();
		} else if (prevProps.active && !this.props.active) {
			this.onHide();
		}
	}

	componentWillUnmount() {
		if (this.props.active) this.onHide();
	}

	onShow() {
		if (!ExecutionEnvironment.canUseDOM) return;
		this.focus();
		document.body.classList.add('fk-modal-visible');
		document.addEventListener('keydown', this.handleKeyDown);

		if (typeof this.props.onShow === 'function') {
			this.props.onShow();
		}
	}

	onHide() {
		if (!ExecutionEnvironment.canUseDOM) return;
		this.restoreFocus();
		document.body.classList.remove('fk-modal-visible');
		document.removeEventListener('keydown', this.handleKeyDown);

		if (typeof this.props.onHide === 'function') {
			this.props.onHide();
		}
	}

	focus() {
		this.lastFocus = document.activeElement;

		if (!this.overlay.hasAttribute('tabIndex')) {
			this.overlay.setAttribute('tabIndex', -1);
		}

		this.content.focus();
	}

	restoreFocus() {
		if (this.lastFocus && this.lastFocus.focus) {
			this.lastFocus.focus();
			this.lastFocus = null;
		}
	}

	handleKeyDown(e) {
		if (this.props.closeOnEscape && e.keyCode === 27) {
			this.props.onRequestClose(e);
		}
	}

	handleOverlayClick(e) {
		if (this.dialog !== e.target && this.overlay !== e.target) return;

		if (this.props.closeOnOverlayClick) {
			this.props.onRequestClose(e);
		}

		if (typeof this.props.onOverlayClick === 'function') {
			this.props.onOverlayClick();
		}
	}

	render() {
		const overlay = cx(styles['modal-overlay'], this.props.overlayClassName, {
			[styles['active']]: this.props.active,
			[styles['scroll-on-body']]: this.props.scrollBarOnBody
		});
		const dialog = cx(styles['modal-dialog'], this.props.dialogClassName);
		const content = cx(styles['modal-content'], this.props.contentClassName);
		const closeButton = cx(styles['close-btn'], this.props.buttonClassName);
		return (
			<div
				ref={c => (this.overlay = c)}
				className={overlay}
				onClick={this.handleOverlayClick}
				id={this.props.overlayId}
			>
				<div ref={c => (this.dialog = c)} className={dialog}>
					{this.props.closeButton ? (
						this.props.manual ? (
							<Button onClick={this.props.onRequestClose} className={closeButton}>
								✕
							</Button>
						) : (
							<Dismiss className={closeButton}>✕</Dismiss>
						)
					) : null}
					<div ref={c => (this.content = c)} className={content} style={this.props.contentStyle}>
						{this.props.children}
					</div>
				</div>
			</div>
		);
	}
}

ModalDialog.defaultProps = {
	closeOnEscape: true,
	closeOnOverlayClick: false,
	scrollBarOnBody: false,
	closeButton: false
};

ModalDialog.propTypes = {
	closeOnEscape: PropTypes.bool,
	closeOnOverlayClick: PropTypes.bool,
	closeButton: PropTypes.bool,
	onOverlayClick: PropTypes.func,
	onHide: PropTypes.func,
	onShow: PropTypes.func,
	overlayClassName: PropTypes.string,
	dialogClassName: PropTypes.string,
	contentClassName: PropTypes.string,
	scrollBarOnBody: PropTypes.bool
};

export default ModalDialog;
